import { StoplossClaim } from "@/types";

/**
 * Pre-submit validation for the Coronis→Aura funding-request push (US #5332, AC1).
 *
 * The sender (ADR-071) needs each claim linked to a client, policy, and claimant and
 * carrying the YTD spend and stop-loss amount. Validating client-side blocks a doomed
 * POST and tells the user exactly what to fix, rather than surfacing a generic Aura 4xx.
 */
const REQUIRED_FIELDS: ReadonlyArray<{ label: string; isMissing: (c: StoplossClaim) => boolean }> = [
  { label: "Client", isMissing: (c) => !c.client_id_ref },
  { label: "Policy", isMissing: (c) => !c.policy_id_ref },
  { label: "Claimant", isMissing: (c) => !c.claimant_id_ref },
  // No YTD check (#7582 / calc-expert review 2026-10-23): the sender does not send
  // YTD at all (aura_sync/funding_request.go), and the API omits
  // total_prior_submissions_ytd when it is 0, so the old check read every first
  // claim as "missing" and disabled Request funding.
  // Mirror the sender: amountForClaim uses total_paid_amount only (AB#5713 /
  // aura_sync/funding_request.go). final_cumulative_total was retired and is no
  // longer consulted; a present zero is accepted, so missing only when
  // total_paid_amount is absent.
  { label: "Stop-loss amount", isMissing: (c) => c.total_paid_amount == null },
];

/** Labels of the required Aura-submit fields missing from a claim (empty array = ready). */
export const missingAuraSubmitFields = (claim: StoplossClaim): string[] =>
  REQUIRED_FIELDS.filter((f) => f.isMissing(claim)).map((f) => f.label);

/**
 * Validate a batch before submitting to Aura. Returns a blocking, actionable message
 * when any claim is missing required fields, or null when all claims are ready.
 * Single-claim: lists that claim's gaps. Bulk: counts blocked claims and the union of
 * missing fields so the user knows what to fix before retrying.
 */
export const validateAuraSubmit = (claims: StoplossClaim[]): string | null => {
  const blocked = claims.map((c) => ({ missing: missingAuraSubmitFields(c) })).filter((r) => r.missing.length > 0);
  if (blocked.length === 0) return null;

  if (claims.length === 1) {
    return `Can't submit to Aura — missing required field(s): ${blocked[0].missing.join(", ")}.`;
  }
  const union = Array.from(new Set(blocked.flatMap((r) => r.missing)));
  return `${blocked.length} of ${claims.length} claim(s) can't be submitted to Aura — missing required field(s): ${union.join(", ")}. Fix and retry.`;
};
